import {
    createComponentById,
    NO_PATTERN_BUSINESS_LOGIC,
    toggleElementVisibility,
} from '@vfde-brix/ws10/core';
import {
    createOfferSummaryCard,
    OFFER_SUMMARY_CARD_HEADLINE_CLASSNAME,
    OFFER_SUMMARY_CARD_HIGHLIGHT_BADGE_CLASSNAME,
    OfferSummaryCard,
} from '@vfde-brix/ws10/offer-summary-card';
import { HtmlId, SummaryCardHtmlId } from '../enums/elementId';
import { State } from '../container/PriceSection/index';
import {
    getUpdatedSummaryCardAddPriceProps,
    getUpdatedSummaryCardHeadlineProps,
    getUpdatedSummaryCardLegalTextProps,
    getUpdatedSummaryCardPriceProps,
    getUpdatedSummaryCardSublineProps,
} from '../util/components/summaryCardHelper';
import { mountLegalTextTooltip, mountPriceSublineTooltip } from './tooltip';
import {
    getPreMarketingPriceUpsellBtnProps,
    getUpdatedPriceButtonProps,
} from '../util/components/buttonHelper';
import { updateFloatingMobileNotificationButton } from './fullwidth-notification';
import { Button } from '@vfde-brix/ws10/button';
import { setDisplayMetacode } from '../util/displayMetacodes';
import { HEADLINE_BASE_CLASSNAME } from '@vfde-brix/ws10/headline';
import { JarvisProduct } from '@vfde-sails/avc-core';
import { PreMarketingType } from '../enums/product';
import { getFastestProduct } from '../container/App/util/reducer';

/**
 * Mounts an Offer Summary Card component.
 * @param id - Wrapper ID of the component
 * @returns Offer Summary Card component if
 * the mount was successful, otherwise null.
 */
export const mountSummaryCard = (id: HtmlId) =>
    createComponentById(createOfferSummaryCard, id, NO_PATTERN_BUSINESS_LOGIC);

const updateDisplayMetacode = (summaryCard: OfferSummaryCard, metacode?: string) => {
    if (!metacode) {
        return;
    }

    const [ headlineEl ] = summaryCard.getContainerElement()
        .getElementsByClassName(OFFER_SUMMARY_CARD_HEADLINE_CLASSNAME) || [];
    const [ headEl ] = headlineEl?.getElementsByClassName(HEADLINE_BASE_CLASSNAME) || [];

    setDisplayMetacode(headEl, metacode);
};

const updateHeadline = (
    summaryCard: OfferSummaryCard,
    { contentEnabled, selectedTariff, preMarketingType }: State,
) => {
    summaryCard.updateHeadline(getUpdatedSummaryCardHeadlineProps(
        contentEnabled,
        selectedTariff?.name,
        preMarketingType,
    ));

    updateDisplayMetacode(summaryCard, selectedTariff?.id);
};

const updateSubline = (
    summaryCard: OfferSummaryCard,
    { contentEnabled, selectedTariff, preMarketingType }: State,
) => {
    summaryCard.updateSubline(getUpdatedSummaryCardSublineProps(
        contentEnabled,
        selectedTariff,
        preMarketingType,
    ));

    contentEnabled && mountPriceSublineTooltip(SummaryCardHtmlId.TARIFF_SUBLINE_TOOLTIP);
};

const updatePrices = (summaryCard: OfferSummaryCard, state: State) => {
    summaryCard.updatePrices(
        getUpdatedSummaryCardPriceProps(state),
        getUpdatedSummaryCardAddPriceProps(state),
    );

    mountLegalTextTooltip(
        SummaryCardHtmlId.LEGAL_TEXT_TOOLTIP,
        state.selectedTariff?.installationFee?.price,
    );
};

const updateHighlightBadge = ({ tariffs, selected, contentEnabled }: State) => {
    const [ badgeEl ] = document.getElementsByClassName(OFFER_SUMMARY_CARD_HIGHLIGHT_BADGE_CLASSNAME) || [];
    const isSelectedFastest = getFastestProduct(tariffs)?.id === selected.tariffId;

    badgeEl && toggleElementVisibility(badgeEl, isSelectedFastest && contentEnabled);
};

/**
 * Updates the Summary Card component
 */
export const updateSummaryCard = (summaryCard: OfferSummaryCard, state: State) => {
    updateHeadline(summaryCard, state);
    updateSubline(summaryCard, state);
    updatePrices(summaryCard, state);
    updateHighlightBadge(state);

    state.selectedTariff && summaryCard.updateLegalText(
        getUpdatedSummaryCardLegalTextProps(state.selectedTariff, state.ff),
    );
};

/**
 * Updates the CtA of the Summary Card
 */
export const updateSummaryCardBusiness = (
    summaryCard: OfferSummaryCard,
    { selectedTariff, gigakombi, avcResult, preMarketingType, userInfo }: State,
    onClick: () => void,
    floatingMobileComp: Button | null,
) => {
    const btnProps = getUpdatedPriceButtonProps(
        selectedTariff,
        gigakombi,
        avcResult,
        onClick,
        preMarketingType,
        userInfo,
    );

    summaryCard.updateButtons([ btnProps ]);

    updateFloatingMobileNotificationButton(
        floatingMobileComp,
        btnProps,
        avcResult,
        selectedTariff?.id,
        userInfo,
    );
};

/**
 * Updates the CtA of the Alternative Summary Card
 */
export const updateAlternativeOffer = (
    summaryCard: OfferSummaryCard,
    altTariff?: JarvisProduct,
    preMarketingType?: PreMarketingType,
) => {
    const showAlternativeOffer = !!altTariff && preMarketingType === PreMarketingType.PRE_FIBER;

    summaryCard.toggleContainer(!showAlternativeOffer);

    if (!altTariff || !showAlternativeOffer) {
        return;
    }

    summaryCard.updateButtons([
        getPreMarketingPriceUpsellBtnProps(altTariff),
    ]);
};
